import { LifeAudio } from './audio/life-audio'
import { LifeClient } from './api/life-client'
import type { LifeState } from './domain/life-state'
import { InteractionEngine } from './input/interaction-engine'
import { createLifeRenderer } from './rendering/life-renderer'
import { LifeSession, OfflineInteractionDelivery } from './state/life-session'
import { StatusPanel } from './ui/status-panel'

export const LOCAL_EMBRYONIC_STATE: LifeState = {
  version: 0,
  ageDays: 0,
  stage: 'embryonic',
  dimensions: {
    energy: 18,
    curiosity: 12,
    connection: 8,
    memory: 0,
    balance: 50,
  },
  updatedAt: '2026-07-30T00:00:00.000Z',
}

export function classifyPointerSpeed(
  deltaX: number,
  deltaY: number,
  elapsedMs: number,
): number {
  if (
    !Number.isFinite(deltaX)
    || !Number.isFinite(deltaY)
    || !Number.isFinite(elapsedMs)
  ) {
    return 0
  }

  if (elapsedMs <= 0) {
    return Math.hypot(deltaX, deltaY) > 0 ? 1 : 0
  }

  const speed = Math.hypot(deltaX, deltaY) / elapsedMs
  return Number.isFinite(speed) ? Math.min(1, Math.max(0, speed)) : 0
}

export function lifeSeedFor(state: Pick<LifeState, 'stage'>): string {
  return `global:${state.stage}`
}

export function toLifeCoordinates(
  clientX: number,
  clientY: number,
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
): { x: number; y: number } {
  const width = Math.max(1, rect.width)
  const height = Math.max(1, rect.height)
  return {
    x: Math.min(1, Math.max(-1, ((clientX - rect.left) / width) * 2 - 1)),
    y: Math.min(1, Math.max(-1, 1 - ((clientY - rect.top) / height) * 2)),
  }
}

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector)
  if (!element) {
    throw new Error(`missing_application_shell:${selector}`)
  }
  return element
}

export function mountExperience(): () => void {
  const canvas = requireElement<HTMLCanvasElement>('#life-canvas')
  const presenceHint = requireElement<HTMLElement>('#presence-hint')
  const soundToggle = requireElement<HTMLButtonElement>('#sound-toggle')
  const statusToggle = requireElement<HTMLButtonElement>('#status-toggle')
  const statusRegion = requireElement<HTMLElement>('#life-status')
  const dimensionsList = requireElement<HTMLElement>('#life-dimensions')
  const lifeDay = requireElement<HTMLElement>('#life-day')
  const lifeStage = requireElement<HTMLElement>('#life-stage')
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  const renderer = createLifeRenderer(canvas, {
    seed: lifeSeedFor(LOCAL_EMBRYONIC_STATE),
    reducedMotion: () => reducedMotion.matches,
  })
  const interactionEngine = new InteractionEngine()
  const audio = new LifeAudio()
  const status = new StatusPanel(statusRegion, dimensionsList, statusToggle)
  const client = new LifeClient()
  let disposed = false
  let soundRequested = false
  let pointerForce = 0
  let previousPointer: { x: number; y: number; at: number } | null = null
  let currentState = LOCAL_EMBRYONIC_STATE
  let lastStateRequestAt = Number.NEGATIVE_INFINITY
  let stateRequest: Promise<void> | null = null

  const applyState = (state: LifeState) => {
    currentState = state
    renderer.setState(state.dimensions)
    audio.update(state.dimensions)
    status.render(state)
    lifeDay.textContent = `DAY ${String(Math.max(0, Math.floor(state.ageDays))).padStart(3, '0')}`
    lifeStage.textContent = state.stage.toUpperCase()
  }

  const refreshState = (force = false): Promise<void> => {
    if (disposed) {
      return Promise.resolve()
    }
    const now = Date.now()
    if (!force && now - lastStateRequestAt < 60_000) {
      return Promise.resolve()
    }
    if (stateRequest) {
      return stateRequest
    }

    lastStateRequestAt = now
    let request!: Promise<void>
    request = client.getState()
      .then((state) => {
        if (!disposed) {
          applyState(state)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (stateRequest === request) {
          stateRequest = null
        }
      })
    stateRequest = request
    return request
  }

  const delivery = new OfflineInteractionDelivery(
    client,
    undefined,
    () => crypto.randomUUID(),
    () => void refreshState(),
  )
  const session = new LifeSession(
    interactionEngine,
    (metrics, windowStartedAt) => delivery.submit(metrics, windowStartedAt),
  )

  applyState(LOCAL_EMBRYONIC_STATE)

  const startRenderer = () => {
    renderer.start()
  }

  const setSoundButton = (enabled: boolean, label?: string) => {
    soundToggle.setAttribute('aria-pressed', String(enabled))
    const text = soundToggle.querySelector('span:last-child')
    if (text) {
      text.textContent = label ?? (enabled ? '关闭声音' : '开启声音')
    }
  }

  const dismissPresenceHint = () => {
    presenceHint.classList.add('is-dismissed')
    presenceHint.setAttribute('aria-hidden', 'true')
  }

  const observePointer = (event: PointerEvent, force: number, active: boolean) => {
    const coordinates = toLifeCoordinates(
      event.clientX,
      event.clientY,
      canvas.getBoundingClientRect(),
    )
    const observedAt = event.timeStamp || performance.now()
    const speed = previousPointer
      ? classifyPointerSpeed(
          event.clientX - previousPointer.x,
          event.clientY - previousPointer.y,
          observedAt - previousPointer.at,
        )
      : 0
    previousPointer = { x: event.clientX, y: event.clientY, at: observedAt }
    renderer.setInput({ ...coordinates, force, speed, active })
    session.observe({
      x: (coordinates.x + 1) / 2,
      y: (1 - coordinates.y) / 2,
      at: observedAt,
      active: force > 0,
    })

    if (event.pointerType === 'touch') {
      dismissPresenceHint()
    }
  }

  const onPointerMove = (event: PointerEvent) => {
    const movementForce = event.buttons > 0
      ? Math.max(pointerForce, event.pressure || 0.5)
      : 0.14
    observePointer(event, movementForce, true)
    dismissPresenceHint()
  }

  const onPointerDown = (event: PointerEvent) => {
    pointerForce = Math.max(0.55, event.pressure || 0)
    observePointer(event, pointerForce, true)
    dismissPresenceHint()
    canvas.setPointerCapture?.(event.pointerId)
  }

  const onPointerEnd = (event: PointerEvent) => {
    pointerForce = 0
    observePointer(event, 0, false)
    previousPointer = null
    canvas.releasePointerCapture?.(event.pointerId)
  }

  const onSoundToggle = async () => {
    soundToggle.disabled = true
    try {
      if (soundRequested) {
        soundRequested = false
        await audio.disable()
        setSoundButton(false)
      } else {
        soundRequested = true
        await audio.enable()
        audio.update(currentState.dimensions)
        setSoundButton(true)
      }
    } catch {
      soundRequested = false
      setSoundButton(false, '声音不可用')
    } finally {
      soundToggle.disabled = false
    }
  }

  const onStatusToggle = () => status.toggle()

  const onMotionPreferenceChange = () => {
    if (document.hidden) {
      return
    }
    renderer.stop()
    startRenderer()
  }

  const onVisibilityChange = () => {
    if (document.hidden) {
      renderer.stop()
      void audio.disable()
      return
    }

    startRenderer()
    if (soundRequested) {
      void audio.enable().then(() => audio.update(currentState.dimensions))
    }
  }

  const dispose = () => {
    if (disposed) {
      return
    }
    disposed = true
    canvas.removeEventListener('pointermove', onPointerMove)
    canvas.removeEventListener('pointerdown', onPointerDown)
    canvas.removeEventListener('pointerup', onPointerEnd)
    canvas.removeEventListener('pointercancel', onPointerEnd)
    canvas.removeEventListener('pointerleave', onPointerEnd)
    soundToggle.removeEventListener('click', onSoundToggle)
    statusToggle.removeEventListener('click', onStatusToggle)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    reducedMotion.removeEventListener('change', onMotionPreferenceChange)
    window.removeEventListener('pagehide', dispose)
    status.dispose()
    renderer.dispose()
    void audio.disable()
    void session.stop().catch(() => undefined)
  }

  canvas.addEventListener('pointermove', onPointerMove, { passive: true })
  canvas.addEventListener('pointerdown', onPointerDown)
  canvas.addEventListener('pointerup', onPointerEnd)
  canvas.addEventListener('pointercancel', onPointerEnd)
  canvas.addEventListener('pointerleave', onPointerEnd)
  soundToggle.addEventListener('click', onSoundToggle)
  statusToggle.addEventListener('click', onStatusToggle)
  document.addEventListener('visibilitychange', onVisibilityChange)
  reducedMotion.addEventListener('change', onMotionPreferenceChange)
  window.addEventListener('pagehide', dispose, { once: true })
  startRenderer()
  status.setWaiting(true)
  void (async () => {
    await refreshState(true)
    if (disposed) {
      return
    }
    await delivery.flushQueued()
    if (!disposed) {
      session.start()
    }
  })()

  return dispose
}

if (document.querySelector('#experience')) {
  mountExperience()
}
